import {repoURL} from '@Libraries/s3-library';
import type {RGBA} from '@Utils/color.util';
import {colorToRgbaArray, rgbaArrayToRgbString} from '@Utils/color.util';
import type {LoadSVGDataResponse} from '@Utils/fabric.util';
import {doPathsHaveSameFill, loadSVGDataFromURL} from '@Utils/fabric.util';
import {getWriteBucket} from '@Utils/s3.util';
import type {FabricObject, Group} from '@postermywall/fabricjs-2';
import {Gradient, Pattern, util} from '@postermywall/fabricjs-2';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import {createSolidFill, createTransparentFill, FillTypes} from '@PosterWhiteboard/classes/fill.types';
import {createGradientFillForSurface, FillSurface} from '@PosterWhiteboard/classes/fill-surface';
import {toFabricColorStops} from '@PosterWhiteboard/classes/fill-gradient';
import {PMW_SHAPE_DEFAULT_FILL} from '@PosterWhiteboard/items/vector-item/vector-item.types';

const VECTOR_MODIFIED_TIMESTAMP = 1689664600;

/**
 * One ramp step darker than PMW_SHAPE_DEFAULT_FILL so a newly enabled outline is visible against the fill.
 * Keep in sync with $ct-neutral-primary ($gray-80, #403F5D) in public/assets/css/components/core/tokens/_content.scss.
 */
export const PMW_SHAPE_DEFAULT_BORDER_FILL: RGBA = [64, 63, 93, 1];

/**
 * PMW_SHAPE_DEFAULT_FILL at alpha 0. Outline-only shapes add with an empty interior; keeping the shape
 * gray's RGB channels means the fill picker starts from the same tone when the user raises the alpha.
 */
export const PMW_SHAPE_TRANSPARENT_FILL: RGBA = [PMW_SHAPE_DEFAULT_FILL[0], PMW_SHAPE_DEFAULT_FILL[1], PMW_SHAPE_DEFAULT_FILL[2], 0];

export enum VectorItemSource {
  GETTY = 'getty',
  PIXABAY = 'pixabay',
  STORYBLOCKS = 'storyblocks',
  STORYBLOCKS_ILLUSTRATIONS = 'storyblocks_illustrations',
  STORYBLOCKS_ILLUSTRATIONS_DASH = 'storyblocks-illustrations',
  PIXABAY_ILLUSTRATIONS = 'pixabay-illustrations',
  GETTY_ILLUSTRATIONS = 'getty-illustrations',
  PMW_SHAPES = 'pmw-shapes',
  UPLOAD = 'upload',
  BRAND_LOGOS = 'brand-logos',
}

export const loadFabricObjectForShape = async (shapeName: string): Promise<FabricObject | Group> => {
  const {paths, metaData} = await loadVectorSVG(shapeName, VectorItemSource.PMW_SHAPES);
  applyDefaultShapeFillToPaths(paths);

  return util.groupSVGElements(paths, {
    height: metaData.height,
    width: metaData.width,
    selectable: true,
  });
};

/**
 * Only masking consumes this loader, and mask generation overrides the fill anyway (applyMaskForFabricObject
 * clones + paints black before compositing). Painting the paths here only fixes what the user sees while the
 * shape is on screen before the mask applies, keeping it consistent with the gray shape thumbnails.
 */
const applyDefaultShapeFillToPaths = (paths: FabricObject[]): void => {
  const fill = rgbaArrayToRgbString(PMW_SHAPE_DEFAULT_FILL);
  for (const path of paths) {
    path.set({fill});
  }
};

export const loadVectorSVG = async (vectorFileName: string, source: VectorItemSource): Promise<LoadSVGDataResponse> => {
  const url = getUrlForVector(vectorFileName, source);
  return loadSVGDataFromURL(url);
};

const getUrlForVector = (fileName: string, source: VectorItemSource): string => {
  const bucket = getWriteBucket();
  let path;

  switch (source) {
    case VectorItemSource.GETTY:
    case VectorItemSource.PIXABAY:
    case VectorItemSource.STORYBLOCKS:
    case VectorItemSource.STORYBLOCKS_ILLUSTRATIONS:
    case VectorItemSource.STORYBLOCKS_ILLUSTRATIONS_DASH:
    case VectorItemSource.PIXABAY_ILLUSTRATIONS:
    case VectorItemSource.GETTY_ILLUSTRATIONS:
    case VectorItemSource.BRAND_LOGOS:
    case VectorItemSource.UPLOAD:
      path = `uploads/${fileName}.svg`;
      break;
    case VectorItemSource.PMW_SHAPES:
      path = `pmwstockmedia/pmwstockshapes/${fileName}.svg`;
      break;
    default:
      throw new Error(`Unhandled source for vector filename: ${fileName}, source: ${source}`);
  }

  return `${repoURL(path, bucket)}ts=${VECTOR_MODIFIED_TIMESTAMP}`;
};

/**
 * True when the SVG artwork is drawn purely with strokes (thin-outline circles, frames, rings).
 * Forcing the uniform shape fill onto such paths would paint their interiors and turn the outline
 * design into a solid silhouette, so these shapes must add with a transparent fill and their stroke
 * width mapped to the item border instead.
 */
export const isStrokeOnlyArtwork = (paths: FabricObject[]): boolean => {
  return paths.length > 0 && paths.every((path) => !path.fill) && paths.some((path) => Boolean(path.stroke));
};

/**
 * The fill a customizable SVG's paths share, as the vector's starting fill. Fabric's SVG parser stores gradient stops last to first, so they are
 * put back in stop order. Patterns never get here: arePathsCustomizable rejects them.
 */
export const getFillObjectForSvgPathFill = (fill: FabricObject['fill']): FillObject => {
  if (!fill) {
    return createTransparentFill();
  }
  if (typeof fill === 'string') {
    return createSolidFill(colorToRgbaArray(fill));
  }
  if (fill instanceof Gradient) {
    const colorStopsInOrder = [...fill.colorStops].sort((first, second) => first.offset - second.offset);
    const colors = colorStopsInOrder.map((colorStop): RGBA => colorToRgbaArray(colorStop.color));
    return createGradientFillForSurface(fill.type === 'linear' ? FillTypes.LINEAR_GRADIENT : FillTypes.RADIAL_GRADIENT, colors, FillSurface.VECTOR);
  }
  throw new Error('An SVG path filled with a pattern has no item fill');
};

export const arePathsCustomizable = (paths: FabricObject[]): boolean => {
  if (typeof paths === 'undefined') {
    return true;
  }

  if (paths[0]?.fill instanceof Pattern) {
    return false;
  }

  return doPathsHaveSameFill(paths);
};

const VECTOR_LINEAR_GRADIENT_HALF_SPAN = 0.75;
const VECTOR_RADIAL_GRADIENT_INNER_RADIUS = 0.1;
const VECTOR_RADIAL_GRADIENT_STOP_SPAN = 0.75;

/**
 * A vector's linear gradient, in Fabric's percentage units so every path of a multi-path SVG ramps over its own height. The line runs from three
 * quarters of the height above the top edge to three quarters down, and color i of n sits at (i + 1) / n along it, as vectors have always drawn
 * them: two colors run from the top edge to three quarters down.
 */
export const getLinearGradientForVectorItem = (colors: RGBA[]): Gradient<'linear'> => {
  return new Gradient<'linear', 'linear'>({
    gradientUnits: 'percentage',
    colorStops: toFabricColorStops(colors.map((color, index) => ({color, offset: (index + 1) / colors.length}))),
    coords: {
      x1: 0,
      y1: -VECTOR_LINEAR_GRADIENT_HALF_SPAN,
      x2: 0,
      y2: VECTOR_LINEAR_GRADIENT_HALF_SPAN,
    },
    type: 'linear',
  });
};

/**
 * A vector's radial gradient, laid over the whole vector within the inner three quarters of the radius, since most SVGs leave space between
 * their control box and their shape. Color i of n sits at the center or at three quarters out, whichever i / n rounds to, as vectors have always
 * drawn them: two colors run from the center to three quarters out.
 */
export const getRadialGradientForVectorItem = (colors: RGBA[], width: number, height: number): Gradient<'radial'> => {
  const x = width / 2;
  const y = height / 2;
  const maxDimension = Math.max(x, y);

  return new Gradient<'radial', 'radial'>({
    colorStops: toFabricColorStops(colors.map((color, index) => ({color, offset: Math.round(index / colors.length) * VECTOR_RADIAL_GRADIENT_STOP_SPAN}))),
    type: 'radial',
    coords: {
      r1: VECTOR_RADIAL_GRADIENT_INNER_RADIUS * maxDimension * 2,
      r2: maxDimension * 2,
      x1: maxDimension,
      y1: maxDimension,
      x2: maxDimension,
      y2: maxDimension,
    },
  });
};
